import React, { useEffect, useState, Suspense } from "react";
import { motion } from "framer-motion";
import {
  MessageSquareText,
  ListChecks,
  Settings as SettingsIcon,
  Moon,
  Sun,
  LogOut,
  FolderTree,
  PanelLeftClose,
  PanelLeft,
  Activity,
  LayoutDashboard,
  Menu,
  Search,
  HelpCircle,
} from "lucide-react";
import { cn } from "./lib/cn";
import { ChatView } from "./components/chat/ChatView";
import { ChatList } from "./components/chat/ChatList";
import { AuthPage } from "./components/auth/AuthPage";
import { OnboardingWizard } from "./components/onboarding/OnboardingWizard";
import {
  getEnvironment,
  setEnvironment,
  type EnvKind,
} from "./components/onboarding/EnvironmentPicker";
import { TokenUsageWidget } from "./components/ui/TokenUsageWidget";
import { TopVpsIndicator } from "./components/ui/TopVpsIndicator";
const SettingsPage = React.lazy(() => import("./components/settings/SettingsPage").then(m => ({ default: m.SettingsPage })));
const FileTree = React.lazy(() => import("./components/filetree/FileTree").then(m => ({ default: m.FileTree })));
const FileEditor = React.lazy(() => import("./components/files/FileEditor").then(m => ({ default: m.FileEditor })));
const DevOpsConsole = React.lazy(() => import("./components/devops/DevOpsConsole").then(m => ({ default: m.DevOpsConsole })));
type DevOpsTab = import("./components/devops/DevOpsConsole").DevOpsTab;
const CommandPalette = React.lazy(() => import("./components/ui/CommandPalette").then(m => ({ default: m.CommandPalette })));
import { DEVOPS_TABS } from "./components/devops/DevOpsConsole";
import { getToken, clearToken, getProfile, setProfile } from "./lib/storage";
import { useSettingsStore } from "./store/settings";
import { useChatsStore } from "./store/chats";

type ViewKey = "chat" | "devops" | "settings";

const NAV: { key: ViewKey; label: string; icon: typeof MessageSquareText; hint: string }[] = [
  { key: "chat", label: "Чат", icon: MessageSquareText, hint: "Главный AI-агент" },
  // Plan tab removed — plan is always visible in right panel of chat
  { key: "devops", label: "DevOps", icon: Activity, hint: "Метрики VPS" },
  { key: "settings", label: "Настройки", icon: SettingsIcon, hint: "API ключи и тема" },
];

const DASHBOARD_HREF = "/dashboard.html";

/**
 * Хук «мобильный экран?» — true когда ширина окна < 768px (Tailwind md).
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 767px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

export function App() {
  const [view, setView] = useState<ViewKey>("chat");
  const [authed, setAuthed] = useState(() => Boolean(getToken()));
  const [authChecked, setAuthChecked] = useState(() => Boolean(getToken()));
  const [env, setEnv] = useState<EnvKind | null>(() => getEnvironment());
  // Онбординг показываем по флагу aicores.onboarded — НЕ по наличию токена
  // (иначе после регистрации токен есть → гайд скипался для всех).
  const [onboarded, setOnboarded] = useState<boolean>(
    () => localStorage.getItem("aicores.onboarded") === "true",
  );
  const isMobile = useIsMobile();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [devopsTab, setDevopsTab] = useState<DevOpsTab>("dashboard");
  const [devopsTabVersion, setDevopsTabVersion] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);
  const [fileTreeOpen, setFileTreeOpen] = useState(false);
  const [openedFile, setOpenedFile] = useState<string | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") return "dark";
    const saved = localStorage.getItem("aicores.theme");
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("aicores.theme", theme);
  }, [theme]);

  // SSO cookie (account) без aicores.token в localStorage
  useEffect(() => {
    if (authChecked) return;
    (async () => {
      if (getToken()) {
        setAuthChecked(true);
        setAuthed(true);
        return;
      }
      try {
        const { fetchMe } = await import("./lib/api");
        const me = await fetchMe();
        setProfile(me);
        setAuthed(true);
      } catch {
        /* нет сессии — покажем AuthPage */
      } finally {
        setAuthChecked(true);
      }
    })();
  }, [authChecked]);

  const historyLoaded = useChatsStore((s) => s.historyLoaded);

  // Кросс-девайс синхронизация
  useEffect(() => {
    if (!authed) return;
    useChatsStore.getState().loadFromServer();
  }, [authed]);

  if (!authChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg text-fg-muted text-small">
        Загрузка…
      </main>
    );
  }

  if (!authed) {
    // ЕДИНАЯ авторизация через account.aicores.ru — никаких локальных форм.
    // После успешного логина account вернёт пользователя на этот же origin
    // и установит cookie aicores_token (Domain=.aicores.ru).
    if (typeof window !== "undefined") {
      const ret = window.location.href;
      const url = "https://account.aicores.ru/login?return_to=" + encodeURIComponent(ret);
      // Заменяем history-entry, чтобы Back не возвращал на сломанную сессию.
      window.location.replace(url);
    }
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg text-fg-muted text-small">
        Перенаправление на account.aicores.ru…
      </main>
    );
  }

  if (!onboarded) {
    return (
      <OnboardingWizard
        onComplete={() => {
          if (!getEnvironment()) {
            setEnvironment("sandbox");
            setEnv("sandbox");
          }
          localStorage.setItem("aicores.onboarded", "true");
          setOnboarded(true);
          setView("chat");
          // ВАЖНО: после онбординга история чатов «пропадала» — на экран попадала
          // пустая initial-сессия из стора, т.к. чат-вью монтируется только сейчас.
          // История в БД цела — перезагружаем список с сервера и открываем
          // последний чат, чтобы пользователь увидел свою переписку, а не пустоту.
          void useChatsStore.getState().loadFromServer();
        }}
      />
    );
  }

  if (!env) {
    setEnvironment("sandbox");
    setEnv("sandbox");
    return null;
  }

  // Пока история чатов грузится с сервера — показываем спиннер вместо пустого чата.
  // Иначе SSO-пользователь видит "обнулённые" чаты на ~1с при каждом переходе из дашборда.
  if (!historyLoaded) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg text-fg-muted text-small">
        Загрузка…
      </main>
    );
  }

  function handleLogout() {
    // ВАЖНО: веб-юзеры авторизуются через SSO-cookie aicores_token (Domain=.aicores.ru),
    // а не только через localStorage. Раньше logout чистил лишь localStorage и звал
    // setAuthed(false) → редирект на /login, но живой cookie тут же возвращал в
    // авторизованное состояние = «страница просто перезагрузилась». Поэтому уводим
    // на централизованный SSO-logout, который гасит cookie на сервере, затем на login.
    try { clearToken(); } catch { /* ignore */ }
    try { localStorage.removeItem("aicores.profile"); } catch { /* ignore */ }
    const loginReturn = window.location.origin;
    const url =
      "https://account.aicores.ru/logout?return_to=" +
      encodeURIComponent(
        "https://account.aicores.ru/login?return_to=" + encodeURIComponent(loginReturn),
      );
    window.location.href = url;
  }

  const profile = getProfile<{ username?: string; email?: string }>();
  const userLabel = profile?.username || profile?.email || "User";

  return (
    <div className="h-screen flex bg-bg text-fg overflow-hidden" style={{ height: "100dvh" }}>
      {/* Backdrop — только на mobile когда сайдбар открыт. */}
      {sidebarOpen && isMobile && (
        <button
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] md:hidden"
        />
      )}

      {/* ═════════════════════ SIDEBAR ═════════════════════ */}
      {sidebarOpen && (
        <aside
          className={cn(
            "flex flex-col border-r border-border/50",
            "bg-bg/95 backdrop-blur-md",
            isMobile
              ? "fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-2xl animate-slide-in-left"
              : "w-64 shrink-0 relative",
          )}
        >
          {/* ─── Brand ──────────────────────────────────────────────────── */}
          <div className="flex items-center gap-2.5 px-3.5 py-3.5 border-b border-border/40">
            <BrandLogo />
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold tracking-tight">AICORES</span>
              <span className="text-[9px] uppercase tracking-wider text-fg-subtle font-mono mt-0.5">
                Agent · IDE
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto p-1.5 rounded-lg hover:bg-bg-subtle text-fg-subtle hover:text-fg transition-colors"
              aria-label="Свернуть меню"
              title="Свернуть"
            >
              <PanelLeftClose className="size-4" />
            </button>
          </div>

          {/* ─── WORKSPACE section ──────────────────────────────────────── */}
          <SidebarSection label="Workspace">
            {NAV.map((item) => {
              const Icon = item.icon;
              const active = view === item.key;
              return (
                <SidebarItem
                  key={item.key}
                  icon={<Icon className="size-4" />}
                  label={item.label}
                  hint={item.hint}
                  active={active}
                  onClick={() => {
                    setView(item.key);
                    // Закрываем sidebar на узком viewport — не полагаемся на isMobile state
                    // (matchMedia change-listener мог отстать на некоторых браузерах).
                    if (typeof window !== "undefined" && window.innerWidth < 768) {
                      setSidebarOpen(false);
                    }
                  }}
                />
              );
            })}
            <a
              href={DASHBOARD_HREF}
              className={cn(
                "w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-small font-medium transition-all relative",
                "text-fg-muted hover:text-fg hover:bg-bg-subtle/50",
              )}
              title="Личный кабинет: токены, оплата, агенты"
            >
              <LayoutDashboard className="size-4 shrink-0 text-fg-subtle" />
              <span className="truncate">Дашборд</span>
            </a>
            <SidebarItem
              icon={<FolderTree className="size-4" />}
              label="Файлы"
              hint="Дерево файлов VPS"
              active={fileTreeOpen}
              onClick={() => {
                setFileTreeOpen(!fileTreeOpen);
                // На mobile закрываем sidebar при открытии file-tree (file-tree сам становится overlay)
                if (typeof window !== "undefined" && window.innerWidth < 768 && !fileTreeOpen) {
                  setSidebarOpen(false);
                }
              }}
            />
          </SidebarSection>

          {/* ─── CHATS section ──────────────────────────────────────────── */}
          {view === "chat" && (
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <SectionLabel label="Chats" />
              <div
                className="flex-1 overflow-y-auto px-2 pb-2"
                onClick={() => {
                  if (typeof window !== "undefined" && window.innerWidth < 768) {
                    setSidebarOpen(false);
                  }
                }}
              >
                <ChatList />
              </div>
            </div>
          )}

          {/* ─── DEVOPS sub-nav (вторичные вкладки) ─────────────────────── */}
          {view === "devops" && (
            <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
              <SectionLabel label="DevOps · разделы" />
              <div className="px-2 space-y-0.5 pb-2">
                {DEVOPS_TABS.filter((t) => !t.topLevel).map((t) => {
                  const Icon = t.icon;
                  const active = devopsTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setDevopsTab(t.key);
                        setDevopsTabVersion((n) => n + 1);
                        if (typeof window !== "undefined" && window.innerWidth < 768) {
                          setSidebarOpen(false);
                        }
                      }}
                      title={t.hint}
                      className={cn(
                        "w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-small font-medium transition-all relative",
                        active
                          ? "text-fg bg-bg-subtle/80"
                          : "text-fg-muted hover:text-fg hover:bg-bg-subtle/50",
                      )}
                    >
                      {active && (
                        <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full bg-grad-cta shadow-accent" />
                      )}
                      <Icon className={cn("size-4 shrink-0", active ? "text-accent-500" : "text-fg-subtle")} />
                      <span className="truncate">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!view.startsWith("chat") && view !== "devops" && <div className="flex-1" />}

          {/* ─── YOU section (footer) ───────────────────────────────────── */}
          <div className="px-2 pb-2 pt-1 border-t border-border/40 space-y-1.5">
            <div className="px-2 pt-2">
              <TokenUsageWidget />
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5">
              <span
                className="size-7 rounded-full grid place-items-center text-white text-[11px] font-semibold shrink-0"
                style={{ backgroundImage: "var(--grad-cta)" }}
                aria-hidden
              >
                {initialsOf(userLabel)}
              </span>
              <span className="text-small text-fg-muted truncate flex-1">{userLabel}</span>
            </div>
            <div className="flex items-center gap-1 px-1">
              <ModeToggleButton />
              <button
                type="button"
                onClick={() => {
                  // Сбросить флаг онбординга и перезагрузиться, чтобы показать
                  // OnboardingWizard ещё раз. Авторизация (токен) и настройки сохраняются.
                  if (confirm("Показать вступительный гайд заново?")) {
                    localStorage.removeItem("aicores.onboarded");
                    window.location.reload();
                  }
                }}
                className="size-9 grid place-items-center rounded-lg text-fg-subtle hover:text-accent-500 hover:bg-bg-subtle transition-colors"
                aria-label="Показать гайд"
                title="Показать вступительный гайд заново"
              >
                <HelpCircle className="size-4" />
              </button>
              <button
                onClick={handleLogout}
                className="size-9 grid place-items-center rounded-lg text-fg-subtle hover:text-danger hover:bg-bg-subtle transition-colors"
                aria-label="Выйти"
                title="Выйти"
              >
                <LogOut className="size-4" />
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* ═════════════════════ FILE TREE PANEL ═════════════════════ */}
      {fileTreeOpen && (
        <>
          {isMobile && (
            <button
              type="button"
              aria-label="Закрыть файлы"
              onClick={() => setFileTreeOpen(false)}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] md:hidden"
            />
          )}
          <aside
            className={cn(
              "flex flex-col border-r border-border/50 bg-bg/95 backdrop-blur-md",
              isMobile
                ? "fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-2xl animate-slide-in-left"
                : "w-60 shrink-0 relative",
            )}
          >
            <div className="px-3 py-2.5 border-b border-border/40 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider font-mono text-fg-subtle">
                Файлы · VPS
              </span>
              <button
                onClick={() => setFileTreeOpen(false)}
                className="p-1 rounded-md hover:bg-bg-subtle text-fg-subtle hover:text-fg transition-colors"
                aria-label="Закрыть"
              >
                <PanelLeftClose className="size-3.5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              <FileTree onFileClick={(p) => setOpenedFile(p)} />
            </div>
          </aside>
        </>
      )}

      {/* ═════════════════════ MAIN ═════════════════════ */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar — без заголовка/breadcrumb. Только утилитарные кнопки.
             Высота уменьшена до h-9 чтобы не съедать высоту чата. */}
        <header className="h-9 px-3 sm:px-4 flex items-center gap-1.5 shrink-0">
          {(!sidebarOpen || isMobile) && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1.5 -ml-1 rounded-lg hover:bg-bg-subtle text-fg-subtle hover:text-fg transition-colors"
              aria-label="Открыть меню"
            >
              {isMobile ? <Menu className="size-5" /> : <PanelLeft className="size-4" />}
            </button>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            {/* VPS / Sandbox / Local — компактный индикатор */}
            <TopVpsIndicator />

            {/* ⌘K command palette */}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 h-8 px-2.5 rounded-lg border border-border/50 bg-bg-subtle/50 text-fg-muted hover:text-fg hover:bg-bg-subtle transition-colors text-small"
              aria-label="Открыть поиск команд"
              title="Поиск команд (⌘K / Ctrl+K)"
            >
              <Search className="size-3.5" />
              <span className="hidden md:inline">Поиск</span>
              <kbd className="hidden md:inline text-[10px] font-mono px-1 py-0.5 rounded bg-bg-card border border-border/60 text-fg-subtle">
                ⌘K
              </kbd>
            </button>

            <button
              type="button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="size-8 grid place-items-center rounded-lg text-fg-muted hover:text-fg hover:bg-bg-subtle transition-colors"
              aria-label="Сменить тему"
              title={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
            >
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
          </div>
        </header>

        {/* View content */}
        <motion.div
          key={view}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.12 }}
          className="flex-1 min-h-0"
        >
          {view === "chat" && <ChatView />}
          {/* plan tab removed — plan always in right panel */}
          {view === "devops" && (
            <div className="h-full overflow-auto">
              <DevOpsConsole initialTab={devopsTab} navVersion={devopsTabVersion} />
            </div>
          )}
          {view === "settings" && (
            <div className="h-full overflow-hidden">
              <SettingsPage
                theme={theme}
                setTheme={setTheme}
                onLogout={handleLogout}
              />
            </div>
          )}
        </motion.div>
      </main>

      {/* FileEditor — модалка просмотра файла при клике в FileTree */}
      <FileEditor path={openedFile} onClose={() => setOpenedFile(null)} />

      {/* Cmd+K command palette */}
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        setView={(v) => setView(v)}
        setDevopsTab={(t) => {
          setDevopsTab(t);
          setDevopsTabVersion((n) => n + 1);
        }}
        setTheme={setTheme}
        currentTheme={theme}
        onLogout={handleLogout}
        onRestartOnboarding={() => {
          localStorage.removeItem("aicores.onboarded");
          window.location.reload();
        }}
      />
    </div>
  );
}

// ─── Sidebar primitives ───────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="px-3.5 pt-3 pb-1 text-[10px] uppercase tracking-wider font-mono text-fg-subtle">
      {label}
    </div>
  );
}

function SidebarSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border/30 pb-2">
      <SectionLabel label={label} />
      <div className="px-2 space-y-0.5">{children}</div>
    </div>
  );
}

function SidebarItem({
  icon,
  label,
  hint,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-small font-medium transition-all",
        active
          ? "text-fg bg-bg-subtle/80"
          : "text-fg-muted hover:text-fg hover:bg-bg-subtle/50",
      )}
      title={hint}
    >
      {/* Accent-bar slева */}
      {active && (
        <motion.span
          layoutId="sidebar-accent"
          className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full bg-grad-cta shadow-accent"
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
        />
      )}
      <span className={cn("shrink-0 transition-colors", active ? "text-accent-500" : "text-fg-subtle group-hover:text-fg")}>
        {icon}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

function BrandLogo() {
  return (
    <div
      className="size-8 rounded-xl grid place-items-center font-bold text-white text-xs shadow-glow"
      style={{ backgroundImage: "var(--grad-cta)" }}
      aria-hidden
    >
      AI
    </div>
  );
}

function PlanPlaceholder() {
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h2 className="text-h1 mb-2">План задачи</h2>
      <p className="text-fg-muted">
        План агента отображается в правой панели чата. Переключитесь на вкладку «Чат» чтобы увидеть план в реальном времени.
      </p>
    </div>
  );
}

function ModeToggleButton() {
  const mode = useSettingsStore((s) => s.displayMode);
  const setMode = useSettingsStore((s) => s.setDisplayMode);
  const next = mode === "chat" ? "developer" : "chat";

  return (
    <button
      type="button"
      onClick={() => setMode(next)}
      className="flex-1 h-9 px-2 rounded-lg flex items-center justify-center gap-1.5 text-[12px] font-medium text-fg-muted hover:text-fg hover:bg-bg-subtle transition-colors"
      title={mode === "chat" ? "В режим разработчика" : "В режим чата"}
    >
      <span>{mode === "chat" ? "💬" : "🖥️"}</span>
      <span>{mode === "chat" ? "Чат" : "Dev"}</span>
    </button>
  );
}

function initialsOf(label: string): string {
  return (
    label
      .trim()
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "U"
  );
}
